import React from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';

const PiiMaskingConfiguration = ({ preferences, onUpdate }) => {
  const piiMasking = preferences?.piiMasking || {};

  const handleToggle = (field, value) => {
    onUpdate('piiMasking', {
      [field]: value
    });
  };

  const maskingOptions = [
    {
      key: 'emailAddresses',
      label: 'Email Addresses',
      description: 'Mask email addresses in logs and exports (e.g., u***@example.com)',
      icon: 'Mail',
      example: 'user@example.com → u***@example.com'
    },
    {
      key: 'phoneNumbers',
      label: 'Phone Numbers',
      description: 'Mask phone numbers in logs and exports (e.g., +1 (***) ***-1234)',
      icon: 'Phone',
      example: '+1 (555) 123-4567 → +1 (***) ***-4567'
    },
    {
      key: 'paymentInfo',
      label: 'Payment Information',
      description: 'Mask credit card and payment details in logs and exports',
      icon: 'CreditCard',
      example: '4532 1234 5678 9012 → **** **** **** 9012'
    }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon name="EyeOff" size={20} className="text-primary" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-foreground">PII Masking Configuration</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Control how personally identifiable information (PII) is masked in system logs and data exports
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Masking Options */}
        <div className="space-y-4">
          {maskingOptions?.map((option) => (
            <div key={option?.key} className="bg-muted/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                  <Icon name={option?.icon} size={16} className="text-primary" />
                </div>
                <div className="flex-1">
                  <Checkbox
                    checked={piiMasking?.[option?.key]}
                    onChange={(checked) => handleToggle(option?.key, checked)}
                    label={option?.label}
                    description={option?.description}
                  />
                  {piiMasking?.[option?.key] && (
                    <div className="mt-2 ml-6 text-xs text-muted-foreground bg-muted/50 rounded px-3 py-2">
                      <span className="font-medium">Preview: </span>
                      {option?.example}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Audit Trail Toggle */}
        <div className="pt-4 border-t border-border">
          <Checkbox
            checked={piiMasking?.auditTrailEnabled}
            onChange={(checked) => handleToggle('auditTrailEnabled', checked)}
            label="Enable PII Masking Audit Trail"
            description="Track when PII masking is applied in system logs for compliance reporting"
          />
        </div>

        {/* Masking Statistics */}
        {Object.values(piiMasking)?.some(v => v === true) && (
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="BarChart3" size={16} className="text-foreground" />
              <h3 className="text-sm font-medium text-foreground">Active Masking Rules</h3>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">
                  {[piiMasking?.emailAddresses, piiMasking?.phoneNumbers, piiMasking?.paymentInfo]?.filter(Boolean)?.length}
                </div>
                <div className="text-xs text-muted-foreground mt-1">Active Rules</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {piiMasking?.auditTrailEnabled ? 'ON' : 'OFF'}
                </div>
                <div className="text-xs text-muted-foreground mt-1">Audit Trail</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">100%</div>
                <div className="text-xs text-muted-foreground mt-1">Coverage</div>
              </div>
            </div>
          </div>
        )}

        {/* Compliance Notice */}
        <div className="bg-muted/50 rounded-lg p-4 flex items-start gap-3">
          <Icon name="Info" size={16} className="text-muted-foreground flex-shrink-0 mt-0.5" />
          <div className="text-xs text-muted-foreground">
            <p className="font-medium mb-1">PII Masking Best Practices</p>
            <p>
              Enable PII masking for all sensitive data types to comply with GDPR, CCPA, and other privacy regulations. 
              Masked data is irreversible in logs and exports, but original data remains accessible in the application 
              for authorized users.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PiiMaskingConfiguration;
